/**
 * Situations boot wiring — second `createSrsSignals` instance with isolated
 * progress storage, level-mirror effect, race-cancelled deck loader,
 * multi-tab storage sync, and section-tab signal.
 *
 * Exposed via a `bootSituations(mainSignals)` factory function rather than
 * module side-effects to avoid a circular ESM init dependency between
 * main.tsx and this module. main.tsx calls `bootSituations(signals)` AFTER
 * its own `createSrsSignals(...)` resolves, so `mainSignals` is always
 * defined when boot effects fire.
 *
 * V1 dead state notes (intentional):
 *   - situationsSignals.currentMode / currentQuizMode are unused (only
 *     Study Mode renders for Situations).
 *   - We do NOT call situationsSignals.loadPreferences() — `currentLevel`
 *     mirrors the main signals and persisting both would create a
 *     hydration race.
 */

import { signal, effect, untracked, type Signal } from '@preact/signals';
import { StorageService } from '@kevvykevwin/lang-core/storage';
import {
  createSrsSignals,
  type SrsSignals
} from '@kevvykevwin/lang-core/srs';

import type { EnglishCardExt, EnglishLevel } from '../types.js';
import {
  isSection,
  type Section,
  type SituationCardExt,
  type SituationCategory
} from './types.js';
import { loadSituationsDeck } from './index.js';

const SECTION_KEY = 'english-section';

export interface SituationsBootResult {
  currentSection: Signal<Section>;
  situationsSignals: SrsSignals<SituationCardExt>;
  situationsStorage: StorageService;
  /** Active Situations category filter ('all' = no filter). Mutating this
   *  reloads the situations deck through the same effect that watches
   *  currentLevel — so changing category resets cardIndex to 0 and rebuilds
   *  the due-deck queue. */
  activeSituationCategory: Signal<SituationCategory | 'all'>;
}

export function bootSituations(
  mainSignals: SrsSignals<EnglishCardExt>
): SituationsBootResult {
  // -------- Section persistence (validated against schema drift) --------
  const stored = localStorage.getItem(SECTION_KEY);
  const initialSection: Section = isSection(stored) ? stored : 'flashcards';
  const currentSection = signal<Section>(initialSection);
  effect(() => {
    localStorage.setItem(SECTION_KEY, currentSection.value);
  });

  // -------- Isolated storage for Situations progress --------
  const situationsStorage = new StorageService({
    prefix: 'english-situations-',
    version: 1,
    keys: {
      // Single-key (no level suffix) — Situations progress is level-agnostic.
      wordProgress: () => 'english-situations-progress',
      preferences: 'english-situations-preferences',
      sessionStats: 'english-situations-session-stats',
      // Share the audio cache with the main app — same TTS endpoint, same
      // text → same audio blobs. No collision risk because audio cache
      // keys on text content, not card id.
      audioCache: 'english-audio-cache'
    }
  });

  // -------- Second SRS signal instance --------
  const situationsSignals = createSrsSignals<SituationCardExt>({
    storage: situationsStorage,
    initialLevel: mainSignals.currentLevel.value,
    initialQuizMode: 'multiple-choice' // unused — V1 has no Situations quiz
  });

  // -------- Active category filter (drives deck reload below) --------
  const activeSituationCategory = signal<SituationCategory | 'all'>('all');

  // -------- Mirror main signals' currentLevel → situations --------
  effect(() => {
    situationsSignals.setLevel(mainSignals.currentLevel.value);
  });

  // -------- Race-cancelled situations deck-load on level change --------
  // Static-barrel imports make loadSituationsDeck synchronous, so the
  // race-cancellation pattern is paranoid (level can't change mid-await).
  // Kept anyway because (a) cheap, (b) defends future async data sources,
  // (c) self-documents the level-change contract.
  //
  // CRITICAL: The body is wrapped in `untracked()` so that signal reads
  // performed by `setDeck` → `rebuildDueDeck` (which reads wordProgress.value)
  // and friends do NOT subscribe this effect to wordProgress. Without
  // untracked(), every `updateWordSRS` call would re-fire this effect and
  // reset wordProgress back to the freshly-initialized state, silently
  // dropping all SRS grades.
  effect(() => {
    const level = situationsSignals.currentLevel.value as EnglishLevel;
    const category = activeSituationCategory.value;
    untracked(() => {
      try {
        const cards =
          category === 'all'
            ? loadSituationsDeck(level)
            : loadSituationsDeck(level, [category]);
        // Re-check level didn't change mid-computation.
        if (situationsSignals.currentLevel.value !== level) return;
        // Initialize progress against the FULL level deck — narrowing to the
        // selected category here would lose stored grades for other categories
        // when the user toggles between chips.
        const fullDeck = loadSituationsDeck(level);
        const progress = situationsStorage.initializeWordProgress<SituationCardExt>(
          level,
          fullDeck
        );
        situationsSignals.setWordProgressMap(progress);
        situationsSignals.setDeck(cards);
      } catch (err) {
        console.error('Situations load failed:', err);
        // Unified notifications — App.tsx renders ONE ToastContainer for
        // mainSignals.notifications. Situations errors flow there too.
        mainSignals.showNotification('Could not load Situations deck', 'error');
      }
    });
  });

  // -------- Multi-tab storage sync --------
  // Mirrors main.tsx:191-197 pattern for `english-word-progress-${level}`.
  // Two tabs reviewing Situations should not diverge silently.
  window.addEventListener('storage', e => {
    if (e.key === 'english-situations-progress') {
      situationsSignals.hydrateProgressForLevel(
        situationsSignals.currentLevel.value
      );
      situationsSignals.rebuildDueDeck();
    }
  });

  return {
    currentSection,
    situationsSignals,
    situationsStorage,
    activeSituationCategory
  };
}
